<template>
    <div class="container mortgage-calculator" :class="calculatorName">

        <div class="row justify-content-center calculator-header">
            <CalculatorHeader>
                <h2>Mortgage Repayment Calculator</h2>
            </CalculatorHeader>
        </div>          
        <div class="row justify-content-center calculator-body">
            <div class="col-md-10 col-xl-8 mb-3">
                <form class="calculator-container">

                    <div class="mb-5" @keyup="resetCalculation" @change="resetCalculation">
                        <InputElement
                            fieldText="How much do you want to borrow?"
                        >
                            <InputTypeNumber currencyPlaceholder placeholder="e.g. 230000" v-model="borrowValue" />
                        </InputElement>
                        
                        <transition name="fade-x" mode="out-in">
                            <InputElement
                                v-if="borrowValue > 0"
                                fieldText="Interest Rate"
                            >
                                <InputTypeNumber placeholder="%" v-model="interestRate" />
                            </InputElement>
                        </transition>        

                        <transition name="fade-x" mode="out-in">
                            <InputElement
                                v-if="borrowValue > 0 && interestRate > 0"
                                fieldText="How many years would you like the mortgage over?"
                                class="pt-4"
                            >
                                <VueSlider
                                    ref="slider"
                                    v-model="years"
                                    v-bind="sliderOptions"
                                    class="mt-4 mt-md-0"
                                    @keyup="resetCalculation" @change="resetCalculation"
                                >
                                    <template #tooltip>
                                        <div style="min-width: 6rem; font-size: small;" v-if="years === null ">Set mortgage duration</div>
                                        <div v-if="years >= 5">{{ years }}</div>                                        
                                    </template>                                 
                                </VueSlider>
                            </InputElement>                 
                        </transition>

                        <transition name="fade-x" mode="out-in">
                            <div class="row" v-if="borrowValue > 0 && interestRate > 0 && years">
                                <div class="col-md-6 col-lg-4">What mortgage type are you looking for?</div>
                                <div class="col-md-6 col-lg-8">
                                    <DropdownMenu                       
                                        class="mt-3"
                                        :dropdownOptions="dropdownOptions"
                                        v-model="repaymentType"
                                    />
                                </div>
                            </div>
                        </transition>

                        <transition name="scale-in" mode="out-in">
                            <div
                                v-if="borrowValue > 0 && interestRate > 0 && years && repaymentType !==''"
                                class="text-center mt-5 mb-3"
                            >                            
                                <div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
                            </div>
                        </transition>                        
                        
                    </div>

                    <transition name="fade-y" mode="out-in">
                        <div v-if="calcCompleted">

                            <ResultsContainer class="mb-5 calc-results-box">
                                <div class="mb-3 results-title">Thank you for your details. With the information provided, your monthly repayment could be:</div>
                                <span class="fcs-price">£{{ monthlyRepayment.toFixed(2) }}</span>
                            </ResultsContainer>                

                            <SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

                        </div>
                    </transition>                        

                </form>                   
            </div>
        </div>

    </div>
</template>

<script>
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import DropdownMenu from '@/components/utilities/dropdownMenu.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'

export default {
	components: {
		CalculatorHeader,
		DropdownMenu,        
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		SubmissionForm,
        VueSlider				
	},
    data() {
        return {
            calculatorName: 'repayment-calculator',
            calcCompleted: false,
            borrowValue: 0,
            interestRate: 0,
            years: null,
            repaymentType: '',
            monthlyRepayment: 0,
            sliderOptions: {
                min: 5,
                max: 40
            },
			dropdownOptions: [
                { value:'', text: "Choose option" },
				{ value:'interest-only', text: "Interest Only" },
				{ value:'repayment', text: "Repayment" }
			]            
        }
    },   
    methods: {
        calculate() {
            this.monthlyRepayment = this.calculateMonthlyRepayment
            this.calcCompleted = true
        },
		formatNumberOutput(number) {
			const newNumber = (new Intl.NumberFormat('en-GB').format(number))
			if ( newNumber === 'NaN' ) { return 0 } return newNumber
		},        
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
        },
		resetCalculation() {
			this.calcCompleted = false;
		}        
    },
    computed: {
		/**
		 * Calculate the value of the monthly repayment
		 * @return {Number}
		 */	        
        calculateMonthlyRepayment() {
            let t = parseFloat(this.interestRate) / 100 / 12
            return parseFloat(this.borrowValue * t) / parseFloat(1 - Math.pow(1 + t, -(12 * this.years)))
        },        
        collateMessageBody() {
            const calculatorData =
            {
                calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
                calculatorResults:
                `
                    How much do you want to borrow? £${this.borrowValue}
                    Interest Rate: ${this.interestRate}%
                    How long do you want to borrow the money for? ${this.years}
                    What mortgage type are you looking for? ${this.repaymentType}
                    Result £${this.monthlyRepayment.toFixed(2)}
                `
            }
            return calculatorData;
        }
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
    @import './styles/slider.scss';
</style>